import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  Braces,
  CheckCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  FileText,
  HelpCircle,
  MinusCircle,
  MoreHorizontal,
  RotateCw,
  ShieldAlert,
  Sparkles,
  Users,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MetricCards } from "@/components/metric-cards";
import { SearchInput } from "@/components/search-input";
import { EnumBadge } from "@/components/badges";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  INTAKE_DEFAULT_TZ,
  intakeWeekToDateLookbackDays,
  todayLocalDate,
  useIntakeDay,
  useIntakeDays,
} from "@/hooks/use-intake";
import { useDebounce } from "@/hooks/use-debounce";
import {
  caseDisplayName,
  caseTypeLabel,
  caseTypeTone,
  failClassLabel,
  failClassTone,
  formatLocalDateLabel,
  formatLocalDateLong,
  formatScheduledFor,
  shiftYmd,
  shortCaseId,
  stediAaaErrorsTitle,
  stediDiscrepancyTitle,
  stediEligibilityErrorsTitle,
  stediInactiveBenefitsTitle,
  stediInsuranceDiscoveryBadge,
  intakeCaseDetailText,
  intakeCaseStatusBadge,
  bvSuccessGroup,
  groupBvSuccessCounts,
} from "@/lib/intake-format";
import { CasePhoneNumbersTable } from "@/components/case-phone-numbers-table";
import { StediSubscriberFields } from "@/components/stedi-subscriber-fields";
import { StediTooltipBadge } from "@/components/stedi-tooltip-badge";
import {
  StediRerunDialog,
  StediResponseModal,
  rerunStediEnrichment,
  type StediRerunOptions,
} from "@/components/stedi-response-modal";
import { cn } from "@/utils";
import {
  isMedicareBvIntakeCase,
  runMedicareStructuredOutput,
} from "@/lib/medicare-structured-output";
import type { FailClass, IntakeCase, VoiceProvider } from "@/types/intake";
import {
  IN_FLIGHT_FAIL_CLASSES,
  RETRYABLE_FAIL_CLASSES,
} from "@/types/intake";

type IntakeBucketFilter = FailClass | "all" | "retryable" | "in_flight";

const IN_FLIGHT_SET = new Set<FailClass>(IN_FLIGHT_FAIL_CLASSES);

function showRetryAllInView(filter: IntakeBucketFilter): boolean {
  if (filter === "retryable" || filter === "telephony_error") return true;
  if (filter === "in_flight") return true;
  return IN_FLIGHT_SET.has(filter as FailClass);
}

const TZ = INTAKE_DEFAULT_TZ;
const PAGE_SIZE = 200;

function pillClasses(active: boolean) {
  return cn(
    "flex shrink-0 cursor-pointer flex-col items-center rounded-md border px-3 py-2 text-xs transition-colors",
    active
      ? "border-primary bg-primary text-primary-foreground"
      : "border-border bg-background hover:bg-muted/50",
  );
}

export default function DailyIntakePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = useMemo(() => todayLocalDate(TZ), []);
  const initialDate = searchParams.get("date") || today;
  const [date, setDate] = useState(initialDate);

  const lookbackDays = useMemo(
    () => intakeWeekToDateLookbackDays(today, TZ),
    [today],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm);
  const [showHidden, setShowHidden] = useState(false);
  const [bucketFilter, setBucketFilter] = useState<IntakeBucketFilter>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [stediModalCaseId, setStediModalCaseId] = useState<string | null>(null);
  const [stediRerunningCaseId, setStediRerunningCaseId] = useState<string | null>(
    null,
  );
  const [stediRerunDialogCaseId, setStediRerunDialogCaseId] = useState<string | null>(
    null,
  );
  const [stediRerunError, setStediRerunError] = useState<string | null>(null);
  const [medicareFlowCaseId, setMedicareFlowCaseId] = useState<string | null>(null);
  const [medicareFlowError, setMedicareFlowError] = useState<string | null>(null);
  const [medicareFlowMessage, setMedicareFlowMessage] = useState<string | null>(null);

  const {
    days,
    loading: daysLoading,
    error: daysError,
    refresh: refreshDays,
  } = useIntakeDays(lookbackDays, { tz: TZ, showHidden });
  const {
    cases,
    loading: dayLoading,
    loadingMore: dayLoadingMore,
    error: dayError,
    hasMore: dayHasMore,
    loadMore: loadMoreCases,
    refresh: refreshDay,
  } = useIntakeDay(date, {
    pageSize: PAGE_SIZE,
    duplicateLookback: 0,
    tz: TZ,
    showHidden,
  });

  // Keep ?date in sync.
  useEffect(() => {
    const current = searchParams.get("date") || today;
    if (current !== date) {
      const next = new URLSearchParams(searchParams);
      next.set("date", date);
      setSearchParams(next, { replace: true });
    }
    // Also clear selection when changing days.
    setSelectedIds(new Set());
    setBucketFilter("all");
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedIds(new Set());
    setBucketFilter("all");
    setMedicareFlowError(null);
    setMedicareFlowMessage(null);
  }, [showHidden]);

  // Keyboard arrows: ←/→ navigate days.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target && (e.target as HTMLElement).tagName === "INPUT") return;
      if (e.target && (e.target as HTMLElement).tagName === "TEXTAREA") return;
      if (e.key === "ArrowLeft") setDate((d) => shiftYmd(d, -1));
      else if (e.key === "ArrowRight") {
        setDate((d) => {
          const next = shiftYmd(d, 1);
          return next > today ? today : next;
        });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [today]);

  const handleRefresh = useCallback(() => {
    void refreshDays();
    void refreshDay();
  }, [refreshDays, refreshDay]);

  const handleMedicareStructuredOutput = useCallback(
    async (caseId: string, force = false) => {
      setMedicareFlowError(null);
      setMedicareFlowMessage(null);
      setMedicareFlowCaseId(caseId);
      try {
        const result = await runMedicareStructuredOutput(caseId, { force });
        await refreshDay();
        setMedicareFlowMessage(
          `Case ${shortCaseId(caseId)}: ${result.status} · ${result.success_category ?? "no category"}`,
        );
      } catch (e: unknown) {
        setMedicareFlowError(
          e instanceof Error ? e.message : "Medicare structured output failed",
        );
        await refreshDay();
      } finally {
        setMedicareFlowCaseId(null);
      }
    },
    [refreshDay],
  );

  const handleStediRerun = useCallback(
    async (caseId: string, options?: StediRerunOptions) => {
      setStediRerunError(null);
      setStediRerunningCaseId(caseId);
      try {
        const result = await rerunStediEnrichment(caseId, options);
        await refreshDay();
        setStediRerunDialogCaseId(null);
        if (result.has_stedi_eligibility_response) {
          setStediModalCaseId(caseId);
        }
      } catch (e: unknown) {
        setStediRerunError(
          e instanceof Error ? e.message : "Stedi rerun failed",
        );
      } finally {
        setStediRerunningCaseId(null);
      }
    },
    [refreshDay],
  );

  // Preferred source for KPIs/buckets: the lightweight `/intake/days` rollup
  // (cheap, cached, full-day truth). If that row hasn't arrived yet OR errored,
  // fall back to deriving counts from the loaded `cases` so the cards never
  // sit at all-zero while data is clearly on the page.
  const dayRow = useMemo(
    () => (days ?? []).find((d) => d.date === date) ?? null,
    [days, date],
  );
  const casesFallback = useMemo(() => {
    if (cases.length === 0) return null;
    const fc: Partial<Record<FailClass, number>> = {};
    // Success-group tallies derived from the loaded rows' success_category.
    const groups = { bv_obtained: 0, inactive: 0, tier2: 0 };
    for (const c of cases) {
      const k = c.fail_class as FailClass;
      fc[k] = (fc[k] ?? 0) + 1;
      if (k === "live_outcome") {
        const g = bvSuccessGroup(c.success_category);
        if (g) groups[g] += 1;
      }
    }
    const get = (k: FailClass) => fc[k] ?? 0;
    const retryable =
      get("telephony_error") + get("wrong_number_retryable") + get("retryable_other");
    const exhausted = get("exhausted_live_agent") + get("exhausted_other");
    const in_flight = get("pending") + get("in_progress") + get("pending_verification");
    return {
      kpis: {
        total: cases.length,
        completed: get("live_outcome"),
        bv_obtained: groups.bv_obtained,
        tier2_success: groups.tier2,
        inactive: groups.inactive,
        not_submitted: get("not_submitted"),
        in_flight,
        retryable,
        exhausted,
        no_dialable_phone: get("no_dialable_phone"),
        duplicate_patients: cases.filter((c) => c.is_duplicate_patient).length,
      },
      fail_classes: fc,
    };
  }, [cases]);

  const kpis = useMemo(() => {
    if (dayRow) {
      const fc = dayRow.fail_classes;
      const groups = groupBvSuccessCounts(dayRow.success_categories);
      return {
        total: dayRow.total,
        completed: fc.live_outcome ?? 0,
        bv_obtained: groups.bv_obtained,
        tier2_success: groups.tier2,
        inactive: groups.inactive,
        not_submitted: fc.not_submitted ?? 0,
        in_flight: dayRow.in_flight,
        retryable: dayRow.retryable,
        exhausted: dayRow.exhausted,
        no_dialable_phone: fc.no_dialable_phone ?? 0,
        duplicate_patients:
          dayRow.duplicates_seen_before + dayRow.duplicates_within_day,
      };
    }
    return casesFallback?.kpis;
  }, [dayRow, casesFallback]);

  const buckets = useMemo(() => {
    const fc =
      dayRow?.fail_classes ?? casesFallback?.fail_classes ?? ({} as Partial<Record<FailClass, number>>);
    return Object.entries(fc).map(([fail_class, count]) => ({
      fail_class: fail_class as FailClass,
      count: count ?? 0,
    }));
  }, [dayRow, casesFallback]);

  const filteredCases = useMemo(() => {
    let rows = cases;
    if (bucketFilter === "retryable") {
      const set = new Set<FailClass>(RETRYABLE_FAIL_CLASSES);
      rows = rows.filter((c) => set.has(c.fail_class));
    } else if (bucketFilter === "in_flight") {
      rows = rows.filter((c) => IN_FLIGHT_SET.has(c.fail_class));
    } else if (bucketFilter !== "all") {
      rows = rows.filter((c) => c.fail_class === bucketFilter);
    }
    const term = debouncedSearch.trim().toLowerCase();
    if (term) {
      rows = rows.filter((c) => {
        return (
          c.case_id.toLowerCase().includes(term) ||
          caseDisplayName(c).toLowerCase().includes(term) ||
          (c.member_id || "").toLowerCase().includes(term) ||
          (c.patient_id || "").toLowerCase().includes(term) ||
          (c.member_bin || "").toLowerCase().includes(term) ||
          (c.member_pcn || "").toLowerCase().includes(term) ||
          (c.patient_address || "").toLowerCase().includes(term) ||
          (c.group_number || "").toLowerCase().includes(term) ||
          (c.payer_name || "").toLowerCase().includes(term) ||
          (c.structured_output_payer_name || "")
            .toLowerCase()
            .includes(term) ||
          (c.product_name || "").toLowerCase().includes(term) ||
          (c.j_code || "").toLowerCase().includes(term) ||
          (c.fail_reason || "").toLowerCase().includes(term) ||
          (c.fail_class || "").toLowerCase().includes(term) ||
          caseTypeLabel(c.case_type).toLowerCase().includes(term) ||
          (c.case_type || "").toLowerCase().includes(term)
        );
      });
    }
    return rows;
  }, [cases, bucketFilter, debouncedSearch]);

  const toggleSelected = useCallback((caseId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  }, []);

  const allFilteredSelected =
    filteredCases.length > 0 &&
    filteredCases.every((c) => selectedIds.has(c.case_id));

  const toggleSelectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (filteredCases.every((c) => next.has(c.case_id))) {
        for (const c of filteredCases) next.delete(c.case_id);
      } else {
        for (const c of filteredCases) next.add(c.case_id);
      }
      return next;
    });
  }, [filteredCases]);

  const sendToRetry = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      navigate(`/retry?case_ids=${encodeURIComponent(ids.join(","))}&from=${date}`);
    },
    [navigate, date],
  );

  const metricItems = useMemo(() => {
    const k = kpis ?? {
      total: 0,
      completed: 0,
      bv_obtained: 0,
      tier2_success: 0,
      inactive: 0,
      not_submitted: 0,
      in_flight: 0,
      retryable: 0,
      exhausted: 0,
      no_dialable_phone: 0,
      duplicate_patients: 0,
    };
    return [
      { label: "Total received", value: k.total, icon: Activity },
      {
        label: "Success",
        value: k.completed,
        icon: CheckCircle,
        color: "text-green-500",
      },
      {
        label: "BV Obtained",
        value: k.bv_obtained,
        icon: CheckCircle2,
        color: "text-green-600",
      },
      {
        label: "Tier-2 Success",
        value: k.tier2_success,
        icon: ShieldAlert,
        color: "text-amber-500",
      },
      {
        label: "Inactive",
        value: k.inactive,
        icon: MinusCircle,
        color: "text-slate-500",
      },
      {
        label: "Not submitted",
        value: k.not_submitted,
        icon: HelpCircle,
        color: "text-amber-500",
      },
      {
        label: "In flight",
        value: k.in_flight,
        icon: Clock,
        color: "text-blue-500",
      },
      {
        label: "Retryable",
        value: k.retryable,
        icon: RotateCw,
        color: "text-amber-600",
        secondary: { value: k.no_dialable_phone, label: "no phone" },
      },
      {
        label: "Exhausted",
        value: k.exhausted,
        icon: XCircle,
        color: "text-red-500",
      },
      {
        label: "Duplicates",
        value: k.duplicate_patients,
        icon: Users,
        color: "text-purple-500",
      },
    ];
  }, [kpis]);

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Daily Intake</h1>
          <p className="text-sm text-muted-foreground">
            {formatLocalDateLong(date)}
            {showHidden ? " · hidden payers only (Medicare, etc.)" : null} ·{" "}
            {dayLoading
              ? "loading…"
              : `${cases.length}${dayHasMore ? "+" : ""} of ${dayRow?.total ?? cases.length} case${
                  (dayRow?.total ?? cases.length) === 1 ? "" : "s"
                } loaded`}
          </p>
          {dayError ? (
            <p className="mt-1 text-xs text-destructive">{dayError}</p>
          ) : null}
          {daysError ? (
            <p className="mt-1 text-xs text-destructive">
              Daily rollup failed: {daysError}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDate((d) => shiftYmd(d, -1))}
            disabled={dayLoading}
            title="Previous day (←)"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDate(today)}
            disabled={dayLoading || date === today}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const next = shiftYmd(date, 1);
              if (next <= today) setDate(next);
            }}
            disabled={dayLoading || date >= today}
            title="Next day (→)"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              "gap-1.5 text-xs font-medium",
              showHidden &&
                "border-violet-500/50 bg-violet-500/[0.12] text-violet-900 dark:border-violet-500/40 dark:bg-violet-950/40 dark:text-violet-100",
            )}
            onClick={() => setShowHidden((v) => !v)}
            disabled={dayLoading || daysLoading}
            aria-pressed={showHidden}
            title="Show only payers hidden from the default dashboard view (e.g. Medicare)"
          >
            {showHidden ? "Hide hidden" : "Show hidden"}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={dayLoading || daysLoading}
          >
            <RotateCw
              className={cn(
                "h-4 w-4",
                (dayLoading || daysLoading) && "animate-spin",
              )}
            />
          </Button>
        </div>
      </div>

      {/* Date strip */}
      <ScrollArea className="-mx-2">
        <div className="flex gap-2 px-2">
          {daysLoading && !days
            ? Array.from({ length: lookbackDays }).map((_, i) => (
                <Skeleton key={i} className="h-[72px] w-[88px] shrink-0" />
              ))
            : (days ?? []).map((d) => {
                const active = d.date === date;
                const hasDuplicates =
                  d.duplicates_seen_before > 0 || d.duplicates_within_day > 0;
                return (
                  <button
                    type="button"
                    key={d.date}
                    onClick={() => setDate(d.date)}
                    className={pillClasses(active)}
                    title={[
                      formatLocalDateLong(d.date),
                      `Total ${d.total}`,
                      `✅ ${d.completed} completed`,
                      `❌ ${d.failed} failed (${d.retryable} retryable, ${d.exhausted} exhausted)`,
                      `⏳ ${d.in_flight} in-flight`,
                      hasDuplicates
                        ? `🔁 ${d.duplicates_seen_before} dup vs prior + ${d.duplicates_within_day} within-day`
                        : "",
                    ]
                      .filter(Boolean)
                      .join("\n")}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wide">
                      {formatLocalDateLabel(d.date)}
                    </span>
                    <span className="mt-0.5 text-base font-bold tabular-nums">
                      {d.total}
                    </span>
                    <span
                      className={cn(
                        "mt-0.5 text-[10px] tabular-nums",
                        active ? "opacity-90" : "text-muted-foreground",
                      )}
                    >
                      ✅{d.completed} ❌{d.failed} ⏳{d.in_flight}
                      {hasDuplicates ? ` 🔁${d.duplicates_seen_before + d.duplicates_within_day}` : ""}
                    </span>
                  </button>
                );
              })}
        </div>
      </ScrollArea>

      <MetricCards metrics={metricItems} loading={daysLoading && !dayRow} />

      {/* Bucket filter strip */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setBucketFilter("all")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-medium",
            bucketFilter === "all"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-background hover:bg-muted/50",
          )}
        >
          All <span className="ml-1 tabular-nums">{cases.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setBucketFilter("retryable")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-medium",
            bucketFilter === "retryable"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
          )}
        >
          Retryable{" "}
          <span className="ml-1 tabular-nums">
            {(kpis?.retryable ?? 0)}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setBucketFilter("in_flight")}
          className={cn(
            "rounded-md border px-3 py-1.5 text-xs font-medium",
            bucketFilter === "in_flight"
              ? "border-primary bg-primary text-primary-foreground"
              : "border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100",
          )}
        >
          In flight{" "}
          <span className="ml-1 tabular-nums">{(kpis?.in_flight ?? 0)}</span>
        </button>
        {buckets
          .filter((b) => b.count > 0)
          .map((b) => (
            <button
              type="button"
              key={b.fail_class}
              onClick={() => setBucketFilter(b.fail_class)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-xs font-medium",
                bucketFilter === b.fail_class
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background hover:bg-muted/50",
              )}
            >
              {failClassLabel(b.fail_class)}
              <span className="ml-1 tabular-nums">{b.count}</span>
            </button>
          ))}
      </div>

      {/* Search + selection toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by Case ID, name, patient ID, member, payer, product, J code, fail reason…"
        />
        {selectedIds.size > 0 ? (
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1 text-sm">
            <span className="font-medium">
              {selectedIds.size} selected
            </span>
            <Button
              size="sm"
              onClick={() => sendToRetry(Array.from(selectedIds))}
              className="gap-1"
            >
              <RotateCw className="h-3.5 w-3.5" />
              Validate &amp; retry
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </Button>
          </div>
        ) : null}
        {showRetryAllInView(bucketFilter) ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              sendToRetry(filteredCases.map((c) => c.case_id))
            }
            disabled={filteredCases.length === 0}
            className="gap-1"
          >
            <RotateCw className="h-3.5 w-3.5" />
            Retry all in view ({filteredCases.length})
          </Button>
        ) : null}
      </div>

      {stediRerunError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          Stedi rerun failed: {stediRerunError}
        </div>
      ) : null}

      {medicareFlowError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          Medicare BV synthesis failed: {medicareFlowError}
        </div>
      ) : null}

      {medicareFlowMessage ? (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-900 dark:text-emerald-100">
          {medicareFlowMessage}
        </div>
      ) : null}

      {/* Cases table */}
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-[1280px] border-collapse">
          <thead>
            <tr className="border-b bg-muted/60 text-left text-xs font-medium text-muted-foreground">
              <th className="w-8 px-2 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all in view"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAllFiltered}
                  disabled={filteredCases.length === 0}
                />
              </th>
              <th className="px-2 py-2">Case ID</th>
              <th className="px-2 py-2">Type</th>
              <th className="px-2 py-2">Patient</th>
              <th className="px-2 py-2">Payer</th>
              <th className="px-2 py-2">Status</th>
              <th className="px-2 py-2">Details</th>
              <th className="px-2 py-2">Attempts</th>
              <th className="px-2 py-2">Provider</th>
              <th className="px-2 py-2">Phone</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {dayLoading && cases.length === 0
              ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b">
                    {Array.from({ length: 11 }).map((_, j) => (
                      <td key={j} className="px-2 py-2">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              : filteredCases.length === 0
                ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="h-24 text-center text-sm text-muted-foreground"
                    >
                      No cases match the current filters.
                    </td>
                  </tr>
                )
                : filteredCases.map((c) => (
                    <IntakeCaseRow
                      key={c.case_id}
                      c={c}
                      showHidden={showHidden}
                      selected={selectedIds.has(c.case_id)}
                      onToggle={() => toggleSelected(c.case_id)}
                      onRetry={() => sendToRetry([c.case_id])}
                      onViewStedi={() => setStediModalCaseId(c.case_id)}
                      onRerunStedi={() => setStediRerunDialogCaseId(c.case_id)}
                      stediRerunning={stediRerunningCaseId === c.case_id}
                      onRunMedicareFlow={(force) =>
                        void handleMedicareStructuredOutput(c.case_id, force)
                      }
                      medicareFlowRunning={medicareFlowCaseId === c.case_id}
                    />
                  ))}
          </tbody>
        </table>
      </div>

      {dayHasMore ? (
        <div className="flex justify-center pb-2">
          <Button
            variant="outline"
            onClick={loadMoreCases}
            disabled={dayLoadingMore}
          >
            {dayLoadingMore
              ? "Loading…"
              : `Load ${PAGE_SIZE} more (${cases.length} of ${dayRow?.total ?? "?"} loaded)`}
          </Button>
        </div>
      ) : null}

      <StediRerunDialog
        caseId={stediRerunDialogCaseId}
        open={stediRerunDialogCaseId != null}
        running={stediRerunningCaseId === stediRerunDialogCaseId}
        onOpenChange={(open) => {
          if (!open && stediRerunningCaseId !== stediRerunDialogCaseId) {
            setStediRerunDialogCaseId(null);
          }
        }}
        onConfirm={(options) => {
          if (!stediRerunDialogCaseId) return;
          void handleStediRerun(stediRerunDialogCaseId, options);
        }}
      />

      <StediResponseModal
        caseId={stediModalCaseId}
        open={stediModalCaseId != null}
        onOpenChange={(open) => {
          if (!open) setStediModalCaseId(null);
        }}
      />
    </div>
  );
}

function IntakeCaseRow({
  c,
  showHidden,
  selected,
  onToggle,
  onRetry,
  onViewStedi,
  onRerunStedi,
  stediRerunning,
  onRunMedicareFlow,
  medicareFlowRunning,
}: {
  c: IntakeCase;
  showHidden: boolean;
  selected: boolean;
  onToggle: () => void;
  onRetry: () => void;
  onViewStedi: () => void;
  onRerunStedi: () => void;
  stediRerunning: boolean;
  onRunMedicareFlow: (force: boolean) => void;
  medicareFlowRunning: boolean;
}) {
  const retryable =
    c.fail_class === "telephony_error" ||
    c.fail_class === "wrong_number_retryable" ||
    c.fail_class === "retryable_other";
  const medicareBvTestCase = showHidden && isMedicareBvIntakeCase(c);
  const detail = intakeCaseDetailText(c);
  return (
    <tr
      className={cn(
        "border-b align-top text-xs",
        selected ? "bg-primary/5" : undefined,
      )}
    >
      <td className="px-2 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label="Select case"
        />
      </td>
      <td className="px-2 py-2">
        <CaseIdCell
          caseId={c.case_id}
          hasStediResponse={c.has_stedi_eligibility_response}
          stediRerunEligible={c.stedi_rerun_eligible}
          stediRerunning={stediRerunning}
          onViewStedi={onViewStedi}
          onRerunStedi={onRerunStedi}
          showMedicareBvAction={medicareBvTestCase}
          medicareFlowRunning={medicareFlowRunning}
          medicareStructuredOutputCompleted={
            !!c.medicare_structured_output_completed
          }
          onRunMedicareFlow={onRunMedicareFlow}
        />
      </td>
      <td className="px-2 py-2">
        <EnumBadge
          value={caseTypeLabel(c.case_type)}
          semanticTone={caseTypeTone(c.case_type)}
        />
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{caseDisplayName(c)}</span>
          {c.is_duplicate_patient ? (
            <span
              title="Same patient (name + DOB) seen previously in window"
              className="inline-flex items-center gap-0.5 rounded-sm bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-800 dark:bg-purple-950/60 dark:text-purple-200"
            >
              <Copy className="h-3 w-3" />
              dup
            </span>
          ) : null}
          {c.stedi_has_subscriber_discrepancies ? (
            <span
              title={stediDiscrepancyTitle(c.stedi_subscriber_discrepancies)}
              className="inline-flex items-center gap-0.5 rounded-sm bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-900 dark:bg-orange-950/60 dark:text-orange-200"
            >
              <AlertTriangle className="h-3 w-3" />
              Stedi mismatch
            </span>
          ) : null}
          {c.stedi_has_aaa_errors ? (
            <StediTooltipBadge
              label="Stedi AAA"
              tooltip={stediAaaErrorsTitle(c.stedi_aaa_errors ?? [])}
              className="bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200"
            />
          ) : null}
          {c.stedi_has_eligibility_errors ? (
            <StediTooltipBadge
              label="Stedi error"
              tooltip={stediEligibilityErrorsTitle(c.stedi_eligibility_errors ?? [])}
              className="bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200"
            />
          ) : null}
          {(() => {
            const disco = stediInsuranceDiscoveryBadge(
              c.stedi_insurance_discovery_status,
              c.stedi_insurance_discovery,
            );
            return disco ? (
              <StediTooltipBadge
                label={disco.label}
                tooltip={disco.tooltip}
                className={disco.className}
              />
            ) : null;
          })()}
          {c.stedi_benefits_inactive ? (
            <span
              title={stediInactiveBenefitsTitle(c.stedi_inactive_benefits)}
              className="inline-flex items-center gap-0.5 rounded-sm bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-900 dark:bg-slate-800 dark:text-slate-100"
            >
              <AlertTriangle className="h-3 w-3" />
              Stedi inactive
            </span>
          ) : null}
          {c.auto_retry_paused_reason ? (
            <span
              title={`Auto-retries paused: ${c.auto_retry_paused_reason}. Use the Retry Console to force a retry.`}
              className="inline-flex items-center gap-0.5 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/60 dark:text-amber-200"
            >
              auto-retry paused
            </span>
          ) : null}
          {c.medicare_structured_output_completed ? (
            <span
              title="Medicare BV structured output synthesized via form-backend"
              className="inline-flex items-center gap-0.5 rounded-sm bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-100"
            >
              BV synthesized
            </span>
          ) : null}
          {c.medicare_structured_output_error ? (
            <span
              title={c.medicare_structured_output_error}
              className="inline-flex items-center gap-0.5 rounded-sm bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-900 dark:bg-red-950/60 dark:text-red-200"
            >
              BV synth failed
            </span>
          ) : null}
        </div>
        {c.patient_id ? (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            Patient ID {c.patient_id}
          </div>
        ) : null}
        {c.stedi_subscriber_field_overlays?.length ? (
          <StediSubscriberFields overlays={c.stedi_subscriber_field_overlays} />
        ) : (
          <>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {c.member_dob || "—"}
              {c.member_id ? ` · ${c.member_id}` : ""}
            </div>
            {c.patient_address ? (
              <div
                className="mt-0.5 max-w-[240px] truncate text-[11px] text-muted-foreground"
                title={c.patient_address}
              >
                {c.patient_address}
              </div>
            ) : null}
            {c.group_number ? (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                Group {c.group_number}
              </div>
            ) : null}
          </>
        )}
        {c.member_bin || c.member_pcn ? (
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            BIN {c.member_bin || "—"} · PCN {c.member_pcn || "—"}
          </div>
        ) : null}
      </td>
      <td className="px-2 py-2">
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-foreground">
            {c.structured_output_payer_name || c.payer_name || "—"}
          </span>
          {c.stedi_payer_id_missing ? (
            <StediTooltipBadge
              label="No Stedi id"
              tooltip="No Stedi eligibility response is stored for this payer — the payer Stedi id was not resolved, so the eligibility check did not run."
              className="bg-violet-100 text-violet-900 dark:bg-violet-950/60 dark:text-violet-200"
            />
          ) : null}
        </div>
        {c.stedi_trading_partner_service_id ? (
          <div
            className="mt-0.5 font-mono text-[11px] text-muted-foreground"
            title="Stedi trading partner service id used for eligibility"
          >
            Stedi id {c.stedi_trading_partner_service_id}
          </div>
        ) : null}
        <div className="text-[11px] text-muted-foreground">
          {c.product_name || "—"}
        </div>
        {c.j_code ? (
          <div className="text-[11px] text-muted-foreground">{c.j_code}</div>
        ) : null}
      </td>
      <td className="px-2 py-2">
        {(() => {
          const status = intakeCaseStatusBadge(c);
          return <EnumBadge value={status.label} semanticTone={status.tone} />;
        })()}
        {c.outbound_scheduled_for ? (
          <div
            className="mt-0.5 text-[11px] text-muted-foreground"
            title={`outbound_scheduled_for=${c.outbound_scheduled_for}`}
          >
            scheduled {formatScheduledFor(c.outbound_scheduled_for)}
          </div>
        ) : c.current_batch_id ? (
          <div
            className="mt-0.5 text-[11px] text-muted-foreground"
            title={`In active retry batch ${c.current_batch_id}`}
          >
            in batch {String(c.current_batch_id).slice(0, 8)}…
          </div>
        ) : null}
      </td>
      <td className="px-2 py-2">
        {detail ? (
          <span className="capitalize">{detail}</span>
        ) : (
          "—"
        )}
      </td>
      <td className="px-2 py-2 tabular-nums">
        {c.attempts}
        {c.all_numbers_exhausted ? (
          <span
            className="ml-1 inline-flex items-center text-amber-700"
            title="All known numbers tried"
          >
            <AlertTriangle className="h-3 w-3" />
          </span>
        ) : null}
      </td>
      <td className="px-2 py-2">
        <ProviderCell
          current={c.voice_provider}
          override={c.voice_provider_override}
        />
      </td>
      <td className="px-2 py-2">
        <CasePhoneNumbersTable view={c} />
      </td>
      <td className="px-2 py-2 text-right">
        {retryable ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={onRetry}
          >
            <RotateCw className="h-3 w-3" />
            Retry
          </Button>
        ) : (
          <span className="text-[11px] text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

function CaseIdCell({
  caseId,
  hasStediResponse,
  stediRerunEligible,
  stediRerunning,
  onViewStedi,
  onRerunStedi,
  showMedicareBvAction,
  medicareFlowRunning,
  medicareStructuredOutputCompleted,
  onRunMedicareFlow,
}: {
  caseId: string;
  hasStediResponse: boolean;
  stediRerunEligible: boolean;
  stediRerunning: boolean;
  onViewStedi: () => void;
  onRerunStedi: () => void;
  showMedicareBvAction: boolean;
  medicareFlowRunning: boolean;
  medicareStructuredOutputCompleted: boolean;
  onRunMedicareFlow: (force: boolean) => void;
}) {
  const hasMenuActions =
    hasStediResponse || stediRerunEligible || showMedicareBvAction;

  return (
    <div className="flex max-w-[140px] items-center gap-0.5">
      <span
        className="min-w-0 flex-1 truncate font-mono text-[11px]"
        title={caseId}
      >
        {shortCaseId(caseId)}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={`Actions for case ${caseId}`}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <DropdownMenuItem asChild>
            <Link to={`/cases?case_id=${encodeURIComponent(caseId)}`}>
              <FileText className="h-3.5 w-3.5" />
              Open in Case Records
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => void navigator.clipboard?.writeText(caseId)}
          >
            <Copy className="h-3.5 w-3.5" />
            Copy case ID
          </DropdownMenuItem>
          {hasMenuActions ? <DropdownMenuSeparator /> : null}
          {hasStediResponse ? (
            <DropdownMenuItem onSelect={onViewStedi}>
              <Braces className="h-3.5 w-3.5" />
              View Stedi response
            </DropdownMenuItem>
          ) : null}
          {stediRerunEligible ? (
            <DropdownMenuItem disabled={stediRerunning} onSelect={onRerunStedi}>
              <RotateCw
                className={cn(
                  "h-3.5 w-3.5",
                  stediRerunning && "animate-spin",
                )}
              />
              {hasStediResponse
                ? "Re-run Stedi check"
                : "Run Stedi check"}
            </DropdownMenuItem>
          ) : null}
          {showMedicareBvAction ? (
            <DropdownMenuItem
              disabled={medicareFlowRunning}
              title={
                medicareStructuredOutputCompleted
                  ? "Re-run Medicare BV synthesis (force)"
                  : "Run Stedi enrichment + form-backend Medicare structured output"
              }
              onSelect={() =>
                onRunMedicareFlow(medicareStructuredOutputCompleted)
              }
            >
              <Sparkles
                className={cn(
                  "h-3.5 w-3.5",
                  medicareFlowRunning && "animate-pulse",
                )}
              />
              {medicareFlowRunning
                ? "Running Medicare BV…"
                : medicareStructuredOutputCompleted
                  ? "Re-run BV synthesis"
                  : "Synthesize BV"}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function providerShortLabel(v: VoiceProvider | null): string {
  if (!v) return "—";
  return v === "asepha_voice" ? "asepha" : v;
}

function ProviderCell({
  current,
  override,
}: {
  current: VoiceProvider | null;
  override: VoiceProvider | null;
}) {
  if (!current && !override) {
    return <span className="text-[11px] text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col gap-0.5 text-[11px]">
      <span className="font-medium">{providerShortLabel(current)}</span>
      {override && override !== current ? (
        <span
          className="text-blue-600 dark:text-blue-300"
          title={`Next retry will route to ${override}`}
        >
          → {providerShortLabel(override)}
        </span>
      ) : null}
    </div>
  );
}
